import { useCallback, useEffect } from 'react'
import useAuthStore from '../../../stores/useAuthStore/useAuthStore'
import { useAxiosInstance } from '../useAxiosInstance'
import {
    GetBillNotesResponse,
    GetInvoiceBillsRequestParams,
    GetInvoiceBillsResponseBody,
    GetPayablesSummaryRequestParams,
    GetPayablesSummaryResponseBody,
    InvoiceDetailsResponse,
} from '../types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toArrayQueryParams } from '../../../utils/toArrayQueryParams'

export const useBillingApi = () => {
    const accessToken = useAuthStore((state) => state.accessToken)
    const axiosInstance = useAxiosInstance(accessToken)
    const queryClient = useQueryClient()

    const customerId =
        useAuthStore((state) => state.customer?.customerId.toString()) ?? ''

    const usePayablesSummaryQuery = (
        params?: GetPayablesSummaryRequestParams,
        options: usePayablesSummaryQueryOpts = { enabled: true }
    ) => {
        const query = useQuery({
            queryKey: ['payables-summary', customerId, params, accessToken],
            queryFn: async () => {
                try {
                    const response =
                        await axiosInstance.get<GetPayablesSummaryResponseBody>(
                            `/api/v1/customers/${customerId}/reports/payables-summary`,
                            { params }
                        )
                    options.onSuccess?.(response.data)
                    return response.data
                } catch (error) {
                    options.onError?.()
                    throw error
                }
            },
            enabled:
                options.enabled &&
                !!accessToken &&
                !!customerId &&
                !!params?.['nodeIds[]'],
            placeholderData: (prev) => prev,
        })

        // Function to download XLSX
        const downloadXlsx = useCallback(async () => {
            const response = await axiosInstance.get<Blob>(
                `/api/v1/customers/${customerId}/reports/payables-summary`,
                {
                    params,
                    headers: {
                        Accept: 'application/vnd.ms-excel',
                    },
                    responseType: 'blob', // Important for file downloads
                }
            )

            // Create a download link
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'payables-summary.xlsx')
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        }, [params])

        useEffect(() => {
            if (query.data?.items) {
                query.data.items.forEach((payable) => {
                    const initialData: InvoiceDetailsResponse = {
                        invoiceNo: payable.invoiceNo,
                        invoiceDocId: payable.invoiceDocId,
                        invoiceDate: payable.billDate,
                        dueDate: payable.dueDate,
                        invoiceTotal: payable.totalInvoiced,
                        currentCharges: payable.charges,
                    }
                    queryClient.setQueryData(
                        [
                            'invoice-details',
                            customerId,
                            accessToken,
                            payable.invoiceNo,
                        ],
                        initialData
                    )
                    void queryClient.invalidateQueries({
                        queryKey: [
                            'invoice-details',
                            customerId,
                            accessToken,
                            payable.invoiceNo,
                        ],
                    })
                })
            }
        }, [params, query.data?.items])

        return { ...query, downloadXlsx }
    }

    const useGetDocument = (
        documentId: number,
        opts: useGetDocumentOpts = {}
    ) => {
        return useQuery({
            queryKey: ['download', customerId, documentId, accessToken],
            queryFn: async () => {
                try {
                    const response = await axiosInstance.get<Blob>(
                        `/api/v1/customers/${customerId}/documents/${documentId.toString()}`,
                        {
                            responseType: 'blob',
                        }
                    )
                    const url = URL.createObjectURL(response.data)
                    const aTag = document.createElement('a')
                    aTag.href = url
                    aTag.download = `invoice-${documentId.toString()}.pdf`
                    aTag.click()
                    URL.revokeObjectURL(url)
                    opts.onSuccess?.()
                    return response.data
                } catch (error) {
                    opts.onError?.()
                    throw error
                }
            },
            enabled: false,
        })
    }

    const useGetDocuments = (
        documentIds: number[],
        opts: useGetDocumentOpts = {}
    ) => {
        const params = {
            'documentIds[]': toArrayQueryParams(documentIds),
        }

        return useQuery({
            queryKey: ['download', customerId, documentIds, accessToken],
            queryFn: async () => {
                try {
                    const response = await axiosInstance.get<Blob>(
                        `/api/v1/customers/${customerId}/documents`,
                        {
                            params,
                            responseType: 'blob',
                        }
                    )
                    const url = URL.createObjectURL(response.data)
                    const aTag = document.createElement('a')
                    aTag.href = url
                    aTag.download = `invoice-documents.zip`
                    aTag.click()
                    URL.revokeObjectURL(url)
                    opts.onSuccess?.()
                    return response.data
                } catch (error) {
                    opts.onError?.()
                    throw error
                }
            },
            enabled: false,
        })
    }

    const useGetInvoice = (invoiceId: string) => {
        return useQuery({
            queryKey: ['invoice-details', customerId, accessToken, invoiceId],
            queryFn: async () => {
                const response =
                    await axiosInstance.get<InvoiceDetailsResponse>(
                        `/api/v1/customers/${customerId}/invoices/${invoiceId}`
                    )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!invoiceId,
        })
    }

    const useGetInvoiceBills = (
        invoiceId: string,
        params: GetInvoiceBillsRequestParams
    ) => {
        return useQuery({
            queryKey: ['bills', customerId, accessToken, invoiceId, params],
            queryFn: async () => {
                const response =
                    await axiosInstance.get<GetInvoiceBillsResponseBody>(
                        `/api/v1/customers/${customerId}/invoices/${invoiceId}/bills`,
                        { params }
                    )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!invoiceId,
            placeholderData: (prev) => prev,
        })
    }

    const useGetBillNotes = (invoiceId: string, billId: string) => {
        return useQuery({
            queryKey: ['notes', customerId, invoiceId, billId, accessToken],
            queryFn: async () => {
                const response = await axiosInstance.get<GetBillNotesResponse>(
                    `/api/v1/customers/${customerId}/invoices/${invoiceId}/bills/${billId}/notes`
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!invoiceId && !!billId,
        })
    }

    return {
        usePayablesSummaryQuery,
        useGetDocument,
        useGetDocuments,
        useGetInvoice,
        useGetInvoiceBills,
        useGetBillNotes,
    }
}

type useGetDocumentOpts = {
    onSuccess?: () => void
    onError?: () => void
}

type usePayablesSummaryQueryOpts = {
    enabled?: boolean
    onSuccess?: (data: GetPayablesSummaryResponseBody) => void
    onError?: () => void
}
